import { useEffect, useMemo, useState } from "react";
import {
  Title,
  Text,
  Stack,
  Group,
  Button,
  Paper,
  Table,
  Badge,
  Modal,
  TextInput,
  Textarea,
  Checkbox,
  Loader,
  ActionIcon,
  Divider,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import { IconPlus, IconEdit, IconTrash } from "@tabler/icons-react";
import { rolesApi } from "../../api/roles.api";
import { modulesApi } from "../../api/modules.api";
import { apiError } from "../../api/client";

export default function RolesPage() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [permsRole, setPermsRole] = useState(null);

  const { data: roles = [], isLoading } = useQuery({
    queryKey: ["roles"],
    queryFn: rolesApi.list,
  });
  const { data: modules = [] } = useQuery({
    queryKey: ["modules", "all"],
    queryFn: modulesApi.listAll,
  });

  const removeMut = useMutation({
    mutationFn: rolesApi.remove,
    onSuccess: () => {
      notifications.show({ color: "green", message: "Role deleted" });
      qc.invalidateQueries({ queryKey: ["roles"] });
    },
    onError: (e) =>
      notifications.show({ color: "red", message: apiError(e) }),
  });

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <div>
          <Title order={2}>Roles & Permissions</Title>
          <Text c="dimmed">
            Roles bundle module-level actions. Assign roles to users in the Users page.
          </Text>
        </div>
        <Button
          leftSection={<IconPlus size={16} />}
          onClick={() => setCreateOpen(true)}
        >
          New Role
        </Button>
      </Group>

      <Paper p={0}>
        {isLoading ? (
          <Group p="lg">
            <Loader size="sm" /> <Text c="dimmed">Loading…</Text>
          </Group>
        ) : (
          <Table striped highlightOnHover verticalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Key</Table.Th>
                <Table.Th>Name</Table.Th>
                <Table.Th>Modules</Table.Th>
                <Table.Th>Description</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {roles.map((r) => (
                <Table.Tr key={r.id}>
                  <Table.Td>
                    <Text ff="monospace" size="sm">
                      {r.key}
                    </Text>
                  </Table.Td>
                  <Table.Td>{r.name}</Table.Td>
                  <Table.Td>
                    <Group gap={4}>
                      {r.modulePermissions.length === 0 && (
                        <Text size="xs" c="dimmed">
                          (none)
                        </Text>
                      )}
                      {r.modulePermissions.map((p) => (
                        <Badge key={p.moduleKey} variant="light">
                          {p.moduleKey}
                        </Badge>
                      ))}
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      {r.description || "—"}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Group gap={4} justify="flex-end">
                      <Button
                        size="xs"
                        variant="subtle"
                        onClick={() => setPermsRole(r)}
                      >
                        Permissions
                      </Button>
                      <ActionIcon
                        variant="subtle"
                        onClick={() => setEditing(r)}
                      >
                        <IconEdit size={16} />
                      </ActionIcon>
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        disabled={r.key === "admin"}
                        onClick={() => {
                          if (
                            confirm(`Delete role ${r.name}? This cannot be undone.`)
                          ) {
                            removeMut.mutate(r.id);
                          }
                        }}
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Paper>

      <RoleFormModal
        opened={createOpen}
        onClose={() => setCreateOpen(false)}
      />
      <RoleFormModal
        opened={!!editing}
        role={editing}
        onClose={() => setEditing(null)}
      />
      <PermissionsModal
        opened={!!permsRole}
        role={permsRole}
        modules={modules}
        onClose={() => setPermsRole(null)}
      />
    </Stack>
  );
}

function RoleFormModal({ opened, onClose, role }) {
  const qc = useQueryClient();
  const isEdit = !!role;

  const form = useForm({
    initialValues: {
      key: role?.key || "",
      name: role?.name || "",
      description: role?.description || "",
    },
    validate: {
      key: (v) =>
        isEdit
          ? null
          : /^[a-z0-9_-]{2,64}$/.test(v)
          ? null
          : "lowercase, digits, '-' or '_'",
      name: (v) => (v.length > 0 ? null : "Required"),
    },
  });

  const mut = useMutation({
    mutationFn: (values) => {
      if (isEdit) {
        const { key, ...rest } = values;
        return rolesApi.update(role.id, rest);
      }
      return rolesApi.create(values);
    },
    onSuccess: () => {
      notifications.show({
        color: "green",
        message: isEdit ? "Role updated" : "Role created",
      });
      qc.invalidateQueries({ queryKey: ["roles"] });
      onClose();
    },
    onError: (e) =>
      notifications.show({ color: "red", message: apiError(e) }),
  });

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={isEdit ? `Edit role · ${role.key}` : "Create role"}
    >
      <form onSubmit={form.onSubmit((v) => mut.mutate(v))}>
        <Stack>
          <TextInput
            label="Key"
            description="Lowercase identifier, cannot be changed later"
            required
            disabled={isEdit}
            {...form.getInputProps("key")}
          />
          <TextInput label="Name" required {...form.getInputProps("name")} />
          <Textarea
            label="Description"
            autosize
            minRows={2}
            {...form.getInputProps("description")}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={mut.isPending}>
              {isEdit ? "Save" : "Create"}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

function PermissionsModal({ opened, onClose, role, modules }) {
  const qc = useQueryClient();

  const initial = useMemo(() => {
    const map = {};
    if (!role) return map;
    for (const m of modules) {
      const existing = role.modulePermissions.find(
        (p) => p.moduleKey === m.key
      );
      const actionMap = {};
      for (const a of [...(m.defaultActions || []), "admin"]) {
        actionMap[a] = !!existing?.actions?.[a];
      }
      map[m.key] = actionMap;
    }
    return map;
  }, [role, modules]);

  const [perms, setPerms] = useState(initial);
  useEffect(() => {
    setPerms(initial);
  }, [initial]);

  const mut = useMutation({
    mutationFn: () => {
      const permissions = Object.entries(perms)
        .map(([moduleKey, actions]) => ({ moduleKey, actions }))
        .filter((p) => Object.values(p.actions).some(Boolean));
      return rolesApi.setPermissions(role.id, permissions);
    },
    onSuccess: () => {
      notifications.show({ color: "green", message: "Permissions saved" });
      qc.invalidateQueries({ queryKey: ["roles"] });
      onClose();
    },
    onError: (e) =>
      notifications.show({ color: "red", message: apiError(e) }),
  });

  if (!role) return null;

  const isAdminRole = role.key === "admin";

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={`Permissions · ${role.name}`}
      size="lg"
    >
      <Stack>
        {isAdminRole && (
          <Text size="sm" c="dimmed">
            The admin role automatically has full access to every module — edits
            here are advisory.
          </Text>
        )}
        {modules.map((m) => {
          const actions = [...(m.defaultActions || []), "admin"];
          return (
            <Paper key={m.key} withBorder p="md">
              <Group justify="space-between" mb={4}>
                <Text fw={600}>{m.name}</Text>
                <Text ff="monospace" size="xs" c="dimmed">
                  {m.key}
                </Text>
              </Group>
              <Text size="xs" c="dimmed" mb="sm">
                {m.description}
              </Text>
              <Divider mb="sm" />
              <Group gap="lg">
                {actions.map((a) => (
                  <Checkbox
                    key={a}
                    label={a}
                    checked={!!perms[m.key]?.[a]}
                    onChange={(e) =>
                      setPerms((prev) => ({
                        ...prev,
                        [m.key]: {
                          ...(prev[m.key] || {}),
                          [a]: e.currentTarget.checked,
                        },
                      }))
                    }
                  />
                ))}
              </Group>
            </Paper>
          );
        })}

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => mut.mutate()} loading={mut.isPending}>
            Save permissions
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
